import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { CheckCircleFilled, PlusOutlined } from "@ant-design/icons";
import { message, notification } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  useActivateSeason,
  type ActivateSeasonVariables,
} from "../../../queries/game-pass/useActivateSeason";
import { useCancelSeason } from "../../../queries/game-pass/useCancelSeason";
import { useCloneSeason } from "../../../queries/game-pass/useCloneSeason";
import { useDeleteSeason } from "../../../queries/game-pass/useDeleteSeason";
import { useGetSeasonsListByStatuses } from "../../../queries/game-pass/useGetSeasonsList";
import {
  filterIdToApiStatuses,
  partitionSeasonsByStatus,
} from "../../../queries/game-pass/seasonsMappers";
import { useUnscheduleSeason } from "../../../queries/game-pass/useUnscheduleSeason";
import {
  getApiErrorMessage,
  shouldOfferForceActivate,
} from "../../../utils/ErrorUtils";
import { SEASONS_FILTER_ID } from "../../../types/gamePass";
import { Button, Modal, useTablistKeyboardNavigation } from "../../shared";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import SeasonCard, {
  type SeasonCardProps,
  type SeasonCardTrailing,
} from "../SeasonCard/SeasonCard";

import {
  DEFAULT_SEASONS_FILTER,
  parseFilterId,
  SEASONS_FILTER_QUERY_PARAM,
  SEASONS_FILTER_TABS,
  type SeasonData,
  type SeasonsFilterId,
} from "./seasonsListData";

import styles from "./SeasonsList.module.css";

type AppContextValue = {
  setActiveSidebarTab: (tabName: string, dropdownOption?: string | false) => void;
  adminReadOnly?: boolean;
};

type SeasonLifecycleAction =
  | { kind: "delete"; season: SeasonData }
  | { kind: "unschedule"; season: SeasonData }
  | { kind: "cancel"; season: SeasonData }
  | {
      kind: "activate";
      season: SeasonData;
      force?: boolean;
      blockerMessage?: string;
    };

/** Inner layout chosen per lifecycle section per filter panel:
 *   - "stack" → Timeline's narrow half-column; cards stack vertically.
 *   - "grid"  → All / Scheduled full-width column; cards lay out 2-per-row.
 *  Lifted to module scope so the section render helpers stay typed
 *  without redefining the alias on every render. */
type SectionLayout = "stack" | "grid";

/** Stable key for the duplicate-season success toast so re-clicking the
 *  icon replaces the existing toast (instead of stacking duplicates) and
 *  the inline "Drafts" link can dismiss it by key. */
const DUPLICATE_TOAST_KEY = "season-duplicate-toast";

export type BuildSeasonCardOptions = {
  /** When provided, `season.trailing === "edit"` produces a pencil-button
   *  descriptor wired to this handler. When omitted, the edit trailing
   *  collapses to `undefined` — matching the original active-card
   *  contract so callers without an edit route never accidentally render
   *  a pencil. The handler receives the full `SeasonData` so the caller
   *  can resolve a route from `season.id` once the edit route ships. */
  onEdit?: (season: SeasonData) => void;
  /** When provided, the season-card title renders the chevron affordance
   *  and fires this handler on activation. Receives the full `SeasonData`
   *  so the caller can resolve a route from `season.id` (the Season
   *  Details page reaches into `SEASON_DETAILS_BY_ID` with that key). */
  onOpen?: (season: SeasonData) => void;
  onDeleteDraft?: (season: SeasonData) => void;
  onUnschedule?: (season: SeasonData) => void;
  onActivateLocked?: (season: SeasonData) => void;
  /** Wires the "Cancel Season" CTA shown alongside Activate on an
   *  Up-Next (locked) season. Omitted callers never render the button. */
  onCancelLocked?: (season: SeasonData) => void;
  /** Wires the footer duplicate icon shown on every lifecycle state.
   *  When provided, the card renders the copy-icon button and fires this
   *  handler with the full `SeasonData` so the caller can seed a
   *  duplicate-season flow. Omitted callers hide the icon. */
  onDuplicate?: (season: SeasonData) => void;
  /** Disables every duplicate icon while a clone request is in-flight so
   *  a double-click can't fire two clone calls (the action has no
   *  confirmation modal). */
  duplicatePending?: boolean;
};

/**
 * Map a serialisable `SeasonData` into the props `SeasonCard` expects.
 *
 * Pure and exported so tests can assert the contract directly without
 * mounting the page. Centralising the conversion here also keeps the
 * card decoupled from the data source — each consumer (Active /
 * Scheduled / Schedule picker) just calls this helper.
 *
 * Both interactive affordances on the card are opt-in via `options`:
 *   - `onOpen` wires the title-row chevron (primary entrypoint to the
 *     Season Details page). Omitted callers (e.g. the Schedule picker
 *     in the wizard) keep a plain non-interactive title.
 *   - `onEdit` wires the bottom-right pencil for `trailing === "edit"`
 *     seasons. Callers without an edit destination leave the option
 *     off and the pencil stays hidden — preserving the original
 *     "no mis-routed edit" guarantee.
 *
 * `onOpen` and `onEdit` may resolve to the same route — for the listing
 * we point both at the Season Details page so the chevron and the
 * pencil don't disagree about where they lead.
 */
export function buildSeasonCardProps(
  season: SeasonData,
  testIdPrefix: string,
  options?: BuildSeasonCardOptions,
): SeasonCardProps {
  let trailing: SeasonCardTrailing | undefined;
  let secondaryAction: SeasonCardProps["secondaryAction"];
  let tertiaryAction: SeasonCardProps["tertiaryAction"];
  if (season.trailing === "locked") {
    trailing = { kind: "locked", label: AppStrings.SEASON_CARD_LOCKED };
  } else if (season.trailing === "edit" && options?.onEdit) {
    const onEdit = options.onEdit;
    trailing = {
      kind: "edit",
      ariaLabel: `${AppStrings.SEASON_CARD_EDIT} ${season.name}`,
      onClick: () => onEdit(season),
    };
  } else if (season.trailing === "seeReports") {
    // The reports route is not yet implemented — the trailing is built
    // without an `onClick` on purpose so the card renders the link in
    // its disabled state. The `disabledTooltip` is surfaced via the
    // button's native `title` so SR / keyboard users get the "Coming
    // soon" hint on focus. Once the destination ships, the trailing
    // here grows an `onClick` (and the button auto-enables).
    trailing = {
      kind: "seeReports",
      label: AppStrings.SEASON_CARD_SEE_REPORTS,
      ariaLabel: `${AppStrings.SEASON_CARD_SEE_REPORTS} — ${season.name}`,
      disabledTooltip: AppStrings.SEASON_CARD_SEE_REPORTS_DISABLED_TOOLTIP,
    };
  }

  if (season.status?.tone === "draft" && options?.onDeleteDraft) {
    secondaryAction = {
      label: AppStrings.EDIT_SEASON_DELETE_SEASON,
      ariaLabel: `${AppStrings.EDIT_SEASON_DELETE_SEASON} ${season.name}`,
      onClick: () => options.onDeleteDraft?.(season),
      tone: "danger",
    };
  } else if (
    season.trailing === "locked" &&
    season.status?.tone === "upNext" &&
    options?.onActivateLocked
  ) {
    secondaryAction = {
      label: AppStrings.SEASON_DETAILS_ACTIVATE,
      ariaLabel: `${AppStrings.SEASON_DETAILS_ACTIVATE} ${season.name}`,
      onClick: () => options.onActivateLocked?.(season),
      tone: "primary",
    };
    // An Up-Next season is ready to go live but hasn't started — the
    // admin can still pull it before activation, so surface a Cancel CTA
    // beneath Activate (unschedule is no longer available once a season
    // is locked into the Up-Next slot).
    if (options?.onCancelLocked) {
      tertiaryAction = {
        label: AppStrings.CANCEL_SEASON,
        ariaLabel: `${AppStrings.CANCEL_SEASON} ${season.name}`,
        onClick: () => options.onCancelLocked?.(season),
        tone: "danger",
      };
    }
  } else if (
    season.trailing === "edit" &&
    !season.status &&
    options?.onUnschedule
  ) {
    secondaryAction = {
      label: AppStrings.EDIT_SEASON_UNSCHEDULE_SEASON,
      ariaLabel: `${AppStrings.EDIT_SEASON_UNSCHEDULE_SEASON} ${season.name}`,
      onClick: () => options.onUnschedule?.(season),
      tone: "danger",
    };
  }

  const onOpenHandler = options?.onOpen;
  const onDuplicateHandler = options?.onDuplicate;

  return {
    name: season.name,
    dateRange: season.dateRange,
    countdown: season.countdown,
    metrics: season.metrics,
    status: season.status,
    trailing,
    secondaryAction,
    tertiaryAction,
    onDuplicate: onDuplicateHandler
      ? () => onDuplicateHandler(season)
      : undefined,
    duplicateAriaLabel: onDuplicateHandler
      ? `${AppStrings.SEASON_CARD_DUPLICATE} ${season.name}`
      : undefined,
    duplicatePending: onDuplicateHandler ? options?.duplicatePending : undefined,
    onOpen: onOpenHandler ? () => onOpenHandler(season) : undefined,
    openAriaLabel: onOpenHandler
      ? `${AppStrings.SEASON_OPEN_ARIA}: ${season.name}`
      : undefined,
    "data-testid": `${testIdPrefix}-${season.id}`,
  };
}

export type SeasonsListProps = {
  /** Per-section override for the active season. When `undefined` the
   *  page falls back to whatever `useGetSeasonsList` returns; pass
   *  `null` to force the active empty-state card or pass a `SeasonData`
   *  to short-circuit the hook entirely. Kept so tests and future
   *  pickers (e.g. the Schedule step in the Create Season wizard) can
   *  exercise the empty / populated branches without spinning up the
   *  API. */
  activeSeason?: SeasonData | null;
  /** Per-section override for the scheduled list. `undefined` → use
   *  the hook's partition; pass `[]` to render the empty-state card;
   *  pass an array to short-circuit. Same rationale as `activeSeason`. */
  scheduledSeasons?: ReadonlyArray<SeasonData>;
  /** Per-section override for the draft list. Same semantics as the
   *  other override props. */
  draftSeasons?: ReadonlyArray<SeasonData>;
  /** Per-section override for the completed list. Same semantics as
   *  the other override props. */
  completedSeasons?: ReadonlyArray<SeasonData>;
  /** Per-section override for the canceled list. Same semantics as
   *  the other override props. */
  canceledSeasons?: ReadonlyArray<SeasonData>;
};

const SeasonsList: FC<SeasonsListProps> = ({
  activeSeason: activeSeasonProp,
  scheduledSeasons: scheduledSeasonsProp,
  draftSeasons: draftSeasonsProp,
  completedSeasons: completedSeasonsProp,
  canceledSeasons: canceledSeasonsProp,
}) => {
  usePageTitle(AppStrings.GAME_PASS_SEASON_CONFIGURATION);

  const navigate = useNavigate();
  const { setActiveSidebarTab, adminReadOnly } = useContext(
    AppContext,
  ) as AppContextValue;
  // UX half of the read-only gamepass guard in PrivateAdminPanelInstance.
  const isReadOnly = adminReadOnly === true;
  const activateSeasonMutation = useActivateSeason();
  const cancelSeasonMutation = useCancelSeason();
  const cloneSeasonMutation = useCloneSeason();
  const deleteSeasonMutation = useDeleteSeason();
  const unscheduleSeasonMutation = useUnscheduleSeason();
  const [pendingLifecycleAction, setPendingLifecycleAction] =
    useState<SeasonLifecycleAction | null>(null);

  // The active filter is driven by the `?filter=<id>` URL search param
  // (rather than local React state) so that:
  //   1. Refreshing the listing preserves the user's tab choice.
  //   2. The browser back button works correctly when returning from a
  //      Season Details page — the listing remounts with the same URL,
  //      so the same tab is re-selected automatically (provided the
  //      Details page bounces back with the `?filter=` query intact;
  //      see the `state.fromFilter` round-trip on `handleOpenSeason`
  //      and the matching reader in `SeasonDetails`).
  //   3. Deep links into a specific tab work without any extra wiring.
  // Unknown / missing values fall back to the documented default
  // (Timeline) — `parseFilterId` is the canonical narrower from the
  // data module so the strip and back-nav consumers agree on what is
  // and isn't a valid filter id.
  //
  // Declared above the seasons fetch so the active filter can be
  // threaded into `useGetSeasonsList`'s `status` query param — when
  // the user switches tabs React Query sees a new key and refetches
  // automatically.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter =
    parseFilterId(searchParams.get(SEASONS_FILTER_QUERY_PARAM)) ??
    DEFAULT_SEASONS_FILTER;

  // Per-tab API call. The page asks `filterIdToApiStatuses` what set
  // of BE statuses populate the current tab and hands that set to
  // `useGetSeasonsListByStatuses`, which fans out one parallel
  // `?status=<X>` query per entry (and unifies the results behind a
  // single hook surface).
  //
  // Concretely:
  //   - Timeline → fires BOTH `?status=active` and
  //     `?status=scheduled` in parallel, so the Active + Scheduled
  //     columns are populated by their authoritative BE slices
  //     (instead of one over-fetched unfiltered call that the page
  //     would then have to partition).
  //   - Scheduled / Drafts / Completed / Canceled → one query each
  //     for their matching BE status.
  //   - All → one unfiltered query (no status filter at all), and
  //     the partition slots records into the matching lifecycle
  //     section client-side.
  //
  // React Query's query key includes the status, so switching tabs
  // either hits the cache (when the tab has been visited within
  // `staleTime`) or refetches the missing slice — admins always see
  // fresh per-tab data without a manual refresh.
  const statusesFilter = filterIdToApiStatuses(activeFilter);
  const seasonsQuery = useGetSeasonsListByStatuses(statusesFilter);

  const partitionedSeasons = useMemo(
    () => partitionSeasonsByStatus(seasonsQuery.data),
    [seasonsQuery.data],
  );

  // Per-section override merge: an explicit prop (even `null` / `[]`)
  // always wins over the hook's partition. Tests and the Schedule
  // picker rely on this — they pass concrete fixtures and expect the
  // network call to be a no-op. The `=== undefined` check is
  // deliberate: `null` (single section) and `[]` (list section) are
  // valid empty-state overrides that should NOT fall back to the hook.
  const activeSeason =
    activeSeasonProp !== undefined ? activeSeasonProp : partitionedSeasons.active;
  const scheduledSeasons =
    scheduledSeasonsProp !== undefined
      ? scheduledSeasonsProp
      : partitionedSeasons.scheduled;
  const draftSeasons =
    draftSeasonsProp !== undefined ? draftSeasonsProp : partitionedSeasons.drafts;
  const completedSeasons =
    completedSeasonsProp !== undefined
      ? completedSeasonsProp
      : partitionedSeasons.completed;
  const canceledSeasons =
    canceledSeasonsProp !== undefined
      ? canceledSeasonsProp
      : partitionedSeasons.canceled;

  // The loading placeholder replaces the panel content only when the
  // page is in pure-API mode (no overrides at all). Any override
  // implies the caller is driving the data themselves (tests,
  // schedule picker, etc.) and doesn't need the page to surface fetch
  // state.
  //
  // Errors deliberately do NOT get their own placeholder — when the
  // request fails the partition naturally falls back to empty arrays
  // / `null`, and each lifecycle section renders its own "You don't
  // have any {section} seasons" empty card (the same UI an admin
  // sees on a clean operator with no seasons yet). The axios
  // interceptor still surfaces the BE error toast, so admins aren't
  // left guessing why the lists are empty.
  const hasAnyOverride =
    activeSeasonProp !== undefined ||
    scheduledSeasonsProp !== undefined ||
    draftSeasonsProp !== undefined ||
    completedSeasonsProp !== undefined ||
    canceledSeasonsProp !== undefined;
  const showLoadingPlaceholder =
    !hasAnyOverride && seasonsQuery.isLoading;

  // Writes the new filter into the URL. The default filter (Timeline)
  // is represented as the *absence* of the query param so the canonical
  // listing URL stays clean (`/game-pass/configuration/seasons`); any
  // other filter is encoded as `?filter=<id>`. `replace: true` keeps
  // tab clicks from cluttering the browser history — the back button
  // still leaves the page, it doesn't unwind through filter changes.
  const handleSelectFilter = useCallback(
    (tabId: SeasonsFilterId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === DEFAULT_SEASONS_FILTER) {
            next.delete(SEASONS_FILTER_QUERY_PARAM);
          } else {
            next.set(SEASONS_FILTER_QUERY_PARAM, tabId);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // The filter strip below is an ARIA tablist; without arrow-key handling
  // keyboard-only users cannot move between tabs. The shared hook owns the
  // ARIA Authoring Practices behaviour (Left/Right wrap, Home/End, skip
  // disabled) so this page and `shared/TabSelector` stay in lockstep.
  const {
    tabRefs: filterTabRefs,
    handleKeyDown: handleFilterKeyDown,
  } = useTablistKeyboardNavigation<SeasonsFilterId>({
    items: SEASONS_FILTER_TABS,
    value: activeFilter,
    onChange: handleSelectFilter,
  });

  useEffect(() => {
    setActiveSidebarTab(
      SIDER_TABS?.GAME_PASS,
      GAME_PASS_DROPDOWN_LABELS.SEASONS,
    );
  }, [setActiveSidebarTab]);

  const goToCreateSeason = useCallback(() => {
    navigate(NAVIGATION_ROUTES.GAME_PASS_SEASON_NEW);
  }, [navigate]);

  // Both the title chevron (every card) and the pencil (Scheduled cards)
  // point at the Season Details page — the chevron is the primary
  // affordance and the pencil is a redundant secondary. Once the seasons
  // API ships the same handler can branch into `edit` vs `view` based on
  // the season's lifecycle if product asks for that nuance.
  //
  // The current filter id is piggy-backed onto the navigation as
  // `state.fromFilter` so the Details page can construct a back URL
  // that lands on the same tab the user came from. The browser back
  // button also restores the listing URL with its `?filter=` intact,
  // so the round-trip works both ways. Falls back to the default tab
  // when no state is present (e.g. deep linked Details URL).
  const handleOpenSeason = useCallback(
    (season: SeasonData) => {
      navigate(
        NAVIGATION_ROUTES.GAME_PASS_SEASON_DETAILS.replace(
          ":seasonId",
          season.id,
        ),
        { state: { fromFilter: activeFilter } },
      );
    },
    [navigate, activeFilter],
  );

  const handleEditScheduled = useCallback(
    (season: SeasonData) => {
      handleOpenSeason(season);
    },
    [handleOpenSeason],
  );

  const handleOpenDeleteDraft = useCallback((season: SeasonData) => {
    setPendingLifecycleAction({ kind: "delete", season });
  }, []);

  const handleOpenUnschedule = useCallback((season: SeasonData) => {
    setPendingLifecycleAction({ kind: "unschedule", season });
  }, []);

  const handleOpenActivateLocked = useCallback((season: SeasonData) => {
    setPendingLifecycleAction({ kind: "activate", season, force: false });
  }, []);

  const handleOpenCancelLocked = useCallback((season: SeasonData) => {
    setPendingLifecycleAction({ kind: "cancel", season });
  }, []);

  // Duplicating a season clones it (challenges + tasks) into a new draft
  // via the clone endpoint. On success we confirm with a bottom-centred
  // toast whose "Drafts" link jumps the listing to the Drafts tab so the
  // admin can find the copy. We use `notification` (not `message`) for
  // this because antd's `message` is hard-pinned to the top of the
  // viewport and its notice transform breaks any attempt to re-anchor it;
  // `notification` positions its own holder for `placement: "bottom"`
  // natively. Failures fall back to the standard top `message.error`
  // surface used across the page (with the BE message when present).
  const handleDuplicateSeason = useCallback(
    async (season: SeasonData) => {
      try {
        const cloned = await cloneSeasonMutation.mutateAsync(season.id);
        // Prefer the server's name for the clone; fall back to the local
        // "{name} - Duplicate" convention if the BE omits it.
        const duplicateName =
          cloned.name?.trim() ||
          `${season.name}${AppStrings.SEASON_DUPLICATE_NAME_SUFFIX}`;
        notification.success({
          key: DUPLICATE_TOAST_KEY,
          placement: "bottom",
          duration: 4,
          className: styles.duplicateToast,
          closeIcon: null,
          icon: <CheckCircleFilled className={styles.duplicateToastIcon} />,
          message: (
            <span className={styles.duplicateToastContent}>
              {`"${duplicateName}" ${AppStrings.SEASON_DUPLICATE_ADDED_TO} `}
              <button
                type="button"
                className={styles.duplicateToastLink}
                onClick={() => {
                  notification.destroy(DUPLICATE_TOAST_KEY);
                  handleSelectFilter(SEASONS_FILTER_ID.DRAFTS);
                }}
                data-testid="seasons-list-duplicate-toast-drafts-link"
              >
                {AppStrings.SEASONS_FILTER_DRAFTS}
              </button>
            </span>
          ),
        });
      } catch (error) {
        message.error(
          getApiErrorMessage(error, AppStrings.SEASON_DUPLICATE_ERROR),
        );
      }
    },
    [cloneSeasonMutation, handleSelectFilter],
  );

  const handleCloseLifecycleModal = useCallback(() => {
    setPendingLifecycleAction(null);
  }, []);

  const handleConfirmLifecycleAction = useCallback(async () => {
    if (!pendingLifecycleAction) return;
    try {
      if (pendingLifecycleAction.kind === "delete") {
        await deleteSeasonMutation.mutateAsync(pendingLifecycleAction.season.id);
      } else if (pendingLifecycleAction.kind === "unschedule") {
        await unscheduleSeasonMutation.mutateAsync(
          pendingLifecycleAction.season.id,
        );
      } else if (pendingLifecycleAction.kind === "cancel") {
        await cancelSeasonMutation.mutateAsync(pendingLifecycleAction.season.id);
      } else {
        const variables: ActivateSeasonVariables = {
          seasonId: pendingLifecycleAction.season.id,
          ...(pendingLifecycleAction.force ? { force: true } : {}),
        };
        await activateSeasonMutation.mutateAsync(variables);
      }
      setPendingLifecycleAction(null);
    } catch (error) {
      if (
        pendingLifecycleAction.kind === "activate" &&
        !pendingLifecycleAction.force &&
        shouldOfferForceActivate(error)
      ) {
        setPendingLifecycleAction({
          kind: "activate",
          season: pendingLifecycleAction.season,
          force: true,
          blockerMessage: getApiErrorMessage(error, ""),
        });
        return;
      }
      message.error(
        getApiErrorMessage(
          error,
          pendingLifecycleAction.kind === "delete"
            ? "Failed to delete season"
            : pendingLifecycleAction.kind === "unschedule"
              ? "Failed to unschedule season"
              : pendingLifecycleAction.kind === "cancel"
                ? "Failed to cancel season"
                : "Failed to activate season",
        ),
      );
    }
  }, [
    activateSeasonMutation,
    cancelSeasonMutation,
    deleteSeasonMutation,
    pendingLifecycleAction,
    unscheduleSeasonMutation,
  ]);

  // Lifecycle sections render through small helpers that pick the inner
  // layout based on the outer panel:
  //   - "stack" → Timeline's narrow half-column. Active is a single
  //     full-width card; Scheduled is a vertical list.
  //   - "grid"  → All / Scheduled full-width column. Cards lay out
  //     2-per-row (matches the Figma spec); single-card sections
  //     (Active) occupy the first cell and the second stays empty,
  //     keeping every section's card widths consistent down the page.
  // Helpers (vs. extracted components) avoid prop-drilling and keep
  // the markup identical to the pre-refactor sections. They live in
  // the component body — not inside the JSX — so the panel switch
  // below reads as ordinary control flow.
  const renderActiveSection = (layout: SectionLayout): ReactNode => {
    const content = activeSeason ? (
      <SeasonCard
        {...buildSeasonCardProps(activeSeason, "seasons-list-active", {
          onOpen: handleOpenSeason,
          ...(isReadOnly
            ? {}
            : {
                onDuplicate: handleDuplicateSeason,
                duplicatePending: cloneSeasonMutation.isPending,
              }),
        })}
      />
    ) : (
      <button
        type="button"
        className={styles.emptyCard}
        onClick={goToCreateSeason}
        data-testid="seasons-list-active-empty"
      >
        <p className={styles.emptyTitle}>
          {AppStrings.ACTIVE_SEASON_EMPTY_TITLE}
        </p>
        <PlusOutlined className={styles.emptyIcon} aria-hidden="true" />
        <p className={styles.emptyCta}>
          {AppStrings.CREATE_A_GAME_PASS_SEASON}
        </p>
      </button>
    );

    return (
      <section
        key="active"
        className={styles.column}
        aria-labelledby="seasons-list-active-title"
        data-testid="seasons-list-section-active"
      >
        <h2 id="seasons-list-active-title" className={styles.columnTitle}>
          <span className={styles.activeDot} aria-hidden="true" />
          {AppStrings.ACTIVE_SEASON}
        </h2>
        {layout === "grid" ? (
          <div className={styles.sectionGrid}>{content}</div>
        ) : (
          content
        )}
      </section>
    );
  };

  const renderScheduledSection = (layout: SectionLayout): ReactNode => {
    const emptyCard = (
      <button
        type="button"
        className={styles.emptyCard}
        onClick={goToCreateSeason}
        data-testid="seasons-list-scheduled-empty"
      >
        <p className={styles.emptyTitle}>
          {AppStrings.SCHEDULED_SEASON_EMPTY_TITLE}
        </p>
        <PlusOutlined className={styles.emptyIcon} aria-hidden="true" />
        <p className={styles.emptyCta}>
          {AppStrings.SCHEDULE_A_GAME_PASS_SEASON}
        </p>
      </button>
    );

    return (
      <section
        key="scheduled"
        className={styles.column}
        aria-labelledby="seasons-list-scheduled-title"
        data-testid="seasons-list-section-scheduled"
      >
        <h2
          id="seasons-list-scheduled-title"
          className={styles.columnTitle}
        >
          {AppStrings.SEASONS_FILTER_SCHEDULED}
        </h2>
        {scheduledSeasons.length > 0 ? (
          <ul
            className={
              layout === "grid" ? styles.sectionGrid : styles.scheduledList
            }
            data-testid="seasons-list-scheduled"
          >
            {scheduledSeasons.map((season) => (
              <li key={season.id} className={styles.scheduledItem}>
                <SeasonCard
                  {...buildSeasonCardProps(season, "seasons-list-scheduled", {
                    onEdit: handleEditScheduled,
                    onOpen: handleOpenSeason,
                    ...(isReadOnly
                      ? {}
                      : {
                          onActivateLocked: handleOpenActivateLocked,
                          onCancelLocked: handleOpenCancelLocked,
                          onUnschedule: handleOpenUnschedule,
                          onDuplicate: handleDuplicateSeason,
                          duplicatePending: cloneSeasonMutation.isPending,
                        }),
                  })}
                />
              </li>
            ))}
          </ul>
        ) : layout === "grid" ? (
          // Wrap in the 2-col sectionGrid so the empty card occupies a
          // single cell (50% width) matching the populated state and
          // the Active section's empty-card layout. Without this wrap
          // the button spans the full column, which reads inconsistent
          // next to the half-width Active empty card.
          <div className={styles.sectionGrid}>{emptyCard}</div>
        ) : (
          emptyCard
        )}
      </section>
    );
  };

  // Drafts mirror the Scheduled section's structure 1:1 — same column
  // shell, same list semantics, same `sectionGrid` vs vertical list
  // switch — so screen readers and the visual hierarchy stay identical
  // across lifecycle sections. The two differences are intentional:
  //   - The data carries `trailing: "edit"` for every draft, so every
  //     card renders the pencil. Both the chevron and the pencil route
  //     to Season Details via `handleEditScheduled` / `handleOpenSeason`
  //     — same as editable Scheduled cards — so the two affordances
  //     never disagree about their destination.
  //   - The empty-state CTA copy points at the Create Season wizard's
  //     "Save as Draft" path (still the only way to author a draft
  //     today), keeping the empty-state navigation contract identical
  //     to Active / Scheduled.
  const renderDraftsSection = (layout: SectionLayout): ReactNode => {
    const emptyCard = (
      <button
        type="button"
        className={styles.emptyCard}
        onClick={goToCreateSeason}
        data-testid="seasons-list-drafts-empty"
      >
        <p className={styles.emptyTitle}>
          {AppStrings.DRAFT_SEASON_EMPTY_TITLE}
        </p>
        <PlusOutlined className={styles.emptyIcon} aria-hidden="true" />
        <p className={styles.emptyCta}>
          {AppStrings.DRAFT_A_GAME_PASS_SEASON}
        </p>
      </button>
    );

    return (
      <section
        key="drafts"
        className={styles.column}
        aria-labelledby="seasons-list-drafts-title"
        data-testid="seasons-list-section-drafts"
      >
        <h2 id="seasons-list-drafts-title" className={styles.columnTitle}>
          {AppStrings.SEASONS_FILTER_DRAFTS}
        </h2>
        {draftSeasons.length > 0 ? (
          <ul
            className={
              layout === "grid" ? styles.sectionGrid : styles.scheduledList
            }
            data-testid="seasons-list-drafts"
          >
            {draftSeasons.map((season) => (
              <li key={season.id} className={styles.scheduledItem}>
                <SeasonCard
                  {...buildSeasonCardProps(season, "seasons-list-drafts", {
                    onEdit: handleEditScheduled,
                    onOpen: handleOpenSeason,
                    ...(isReadOnly
                      ? {}
                      : {
                          onDeleteDraft: handleOpenDeleteDraft,
                          onDuplicate: handleDuplicateSeason,
                          duplicatePending: cloneSeasonMutation.isPending,
                        }),
                  })}
                />
              </li>
            ))}
          </ul>
        ) : layout === "grid" ? (
          // See `renderScheduledSection` — wrap in sectionGrid so the
          // empty card stays half-width and consistent with the
          // populated 2-up layout.
          <div className={styles.sectionGrid}>{emptyCard}</div>
        ) : (
          emptyCard
        )}
      </section>
    );
  };

  // Completed cards are read-only summaries of past seasons. Both
  // interactive affordances on the card behave differently from
  // Scheduled / Drafts:
  //   - The title chevron still navigates to Season Details (the
  //     `SEASON_DETAILS_BY_ID` map has locked-mode entries for every
  //     completed season — see `seasonsListData.ts`).
  //   - The bottom-row "See Reports" link is rendered disabled by
  //     `buildSeasonCardProps` (`trailing === "seeReports"` with no
  //     `onClick`) so users see the affordance from the Figma even
  //     while the reports page is being built.
  // The empty branch renders the static placeholder text instead of an
  // interactive card — there is no "create a completed season" path,
  // so the Active / Scheduled / Drafts empty-card pattern (with its
  // PlusOutlined CTA) would be misleading here.
  const renderCompletedSection = (layout: SectionLayout): ReactNode => (
    <section
      key="completed"
      className={styles.column}
      aria-labelledby="seasons-list-completed-title"
      data-testid="seasons-list-section-completed"
    >
      <h2 id="seasons-list-completed-title" className={styles.columnTitle}>
        {AppStrings.SEASONS_FILTER_COMPLETED}
      </h2>
      {completedSeasons.length > 0 ? (
        <ul
          className={
            layout === "grid" ? styles.sectionGrid : styles.scheduledList
          }
          data-testid="seasons-list-completed"
        >
          {completedSeasons.map((season) => (
            <li key={season.id} className={styles.scheduledItem}>
              <SeasonCard
                {...buildSeasonCardProps(season, "seasons-list-completed", {
                  onOpen: handleOpenSeason,
                  ...(isReadOnly
                    ? {}
                    : {
                        onDuplicate: handleDuplicateSeason,
                        duplicatePending: cloneSeasonMutation.isPending,
                      }),
                })}
              />
            </li>
          ))}
        </ul>
      ) : (
        // Completed's empty branch is a thin dashed-text strip (no big
        // card, no CTA button) — Active / Scheduled / Drafts share the
        // 50% `sectionGrid` wrap because their empty card is large and
        // matches the populated grid cell width. Squeezing this slim
        // strip into a half-cell leaves an awkward gap, so it
        // intentionally spans the full column instead.
        <p
          className={styles.emptyText}
          data-testid="seasons-list-completed-empty"
        >
          {AppStrings.COMPLETED_SEASON_EMPTY_TITLE}
        </p>
      )}
    </section>
  );

  // Canceled cards are read-only summaries of seasons that were ended
  // before completion. They mirror the Completed section's contract
  // exactly:
  //   - The title chevron navigates to Season Details (locked mode).
  //   - The bottom-row "See Reports" link is rendered disabled (the
  //     reports route is shared with Completed and not yet shipped).
  //   - The empty branch is the same static placeholder copy — there
  //     is no "create a canceled season" path, so a CTA card would be
  //     misleading. Only the data-test ids, header label, and empty
  //     copy differ from `renderCompletedSection`.
  const renderCanceledSection = (layout: SectionLayout): ReactNode => (
    <section
      key="canceled"
      className={styles.column}
      aria-labelledby="seasons-list-canceled-title"
      data-testid="seasons-list-section-canceled"
    >
      <h2 id="seasons-list-canceled-title" className={styles.columnTitle}>
        {AppStrings.SEASONS_FILTER_CANCELED}
      </h2>
      {canceledSeasons.length > 0 ? (
        <ul
          className={
            layout === "grid" ? styles.sectionGrid : styles.scheduledList
          }
          data-testid="seasons-list-canceled"
        >
          {canceledSeasons.map((season) => (
            <li key={season.id} className={styles.scheduledItem}>
              <SeasonCard
                {...buildSeasonCardProps(season, "seasons-list-canceled", {
                  onOpen: handleOpenSeason,
                  ...(isReadOnly
                    ? {}
                    : {
                        onDuplicate: handleDuplicateSeason,
                        duplicatePending: cloneSeasonMutation.isPending,
                      }),
                })}
              />
            </li>
          ))}
        </ul>
      ) : (
        // Same rationale as the Completed empty branch — a slim
        // dashed-text strip rather than a half-width CTA card so the
        // empty state doesn't visually compete with the populated
        // grid above it.
        <p
          className={styles.emptyText}
          data-testid="seasons-list-canceled-empty"
        >
          {AppStrings.CANCELED_SEASON_EMPTY_TITLE}
        </p>
      )}
    </section>
  );

  // Decide what each filter shows. Timeline keeps the original
  // 2-column grid (Active | Scheduled) with stacked inner cards.
  // "All" stacks every lifecycle section (Active + Scheduled +
  // Drafts + Completed + Canceled) and asks each section for its
  // 2-up grid layout. "Scheduled", "Drafts", "Completed" and
  // "Canceled" each show their owning section only in grid mode.
  // Any unknown filter falls back to a static placeholder so the
  // panel is never silently populated with sections that don't
  // belong to it (a real regression risk before this refactor).
  let panelClassName: string;
  let panelContent: ReactNode;
  // The loading placeholder short-circuits the per-filter switch
  // below so admins don't see a flash of empty-state cards while the
  // first fetch is in flight. On error we intentionally fall through
  // to the normal render — each lifecycle section already owns a
  // "You don't have any {section} seasons" empty-card branch which
  // serves as the canonical "nothing to show" UI for both the
  // legitimate empty case and the failed-fetch case.
  if (showLoadingPlaceholder) {
    panelClassName = styles.stack;
    panelContent = (
      <p
        className={styles.panelPlaceholder}
        data-testid="seasons-list-panel-loading"
      >
        {AppStrings.SEASONS_LIST_LOADING}
      </p>
    );
  } else {
    switch (activeFilter) {
      case SEASONS_FILTER_ID.TIMELINE:
        panelClassName = styles.timeline;
        panelContent = (
          <>
            {renderActiveSection("stack")}
            {renderScheduledSection("stack")}
          </>
        );
        break;
      case SEASONS_FILTER_ID.ALL:
        panelClassName = styles.stack;
        panelContent = (
          <>
            {renderActiveSection("grid")}
            {renderScheduledSection("grid")}
            {renderDraftsSection("grid")}
            {renderCompletedSection("grid")}
            {renderCanceledSection("grid")}
          </>
        );
        break;
      case SEASONS_FILTER_ID.SCHEDULED:
        panelClassName = styles.stack;
        panelContent = renderScheduledSection("grid");
        break;
      case SEASONS_FILTER_ID.DRAFTS:
        panelClassName = styles.stack;
        panelContent = renderDraftsSection("grid");
        break;
      case SEASONS_FILTER_ID.COMPLETED:
        panelClassName = styles.stack;
        panelContent = renderCompletedSection("grid");
        break;
      case SEASONS_FILTER_ID.CANCELED:
        panelClassName = styles.stack;
        panelContent = renderCanceledSection("grid");
        break;
      default:
        panelClassName = styles.stack;
        panelContent = (
          <p
            className={styles.panelPlaceholder}
            data-testid="seasons-list-panel-placeholder"
          >
            {AppStrings.SEASONS_FILTER_NO_DATA}
          </p>
        );
    }
  }

  return (
    <>
      <div className={styles.page} data-testid="seasons-list">
        <header className={styles.header}>
          <div className={styles.headerActions}>
            <Button
              intent="primary"
              onClick={goToCreateSeason}
              disabled={isReadOnly}
              title={isReadOnly ? AppStrings.READ_ONLY_ACCESS_TOOLTIP : undefined}
              data-testid="seasons-list-create-season"
            >
              {AppStrings.CREATE_SEASON}
            </Button>
          </div>
        </header>

        <div
          className={styles.tabs}
          role="tablist"
          aria-label={AppStrings.SEASONS_FILTER_BAR_ARIA}
          data-testid="seasons-list-filter-tabs"
        >
          {SEASONS_FILTER_TABS.map((tab, index) => {
            const isActive = tab.id === activeFilter;
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  filterTabRefs.current[index] = node;
                }}
                // The tabpanel below points at the active tab via
                // `aria-labelledby`. Without an `id` here that reference
                // resolves to nothing and screen readers don't announce
                // the panel name.
                id={`seasons-list-filter-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.tab} ${
                  isActive ? styles.tabActive : ""
                }`}
                onClick={() => handleSelectFilter(tab.id)}
                onKeyDown={(event) => handleFilterKeyDown(event, index)}
                data-testid={`seasons-list-filter-${tab.id}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          className={panelClassName}
          role="tabpanel"
          aria-labelledby={`seasons-list-filter-${activeFilter}`}
          data-testid={`seasons-list-panel-${activeFilter}`}
        >
          {panelContent}
        </div>

        <Modal
          open={Boolean(pendingLifecycleAction)}
          onClose={handleCloseLifecycleModal}
          intent="destructive"
          size="sm"
          destroyOnHidden
          title={
            <span>
              {pendingLifecycleAction?.kind === "delete"
                ? AppStrings.DELETE_SEASON_MODAL_TITLE
                : pendingLifecycleAction?.kind === "unschedule"
                  ? AppStrings.UNSCHEDULE_SEASON_MODAL_TITLE
                  : pendingLifecycleAction?.kind === "cancel"
                    ? AppStrings.CANCEL_SEASON_MODAL_TITLE
                    : pendingLifecycleAction?.force
                      ? AppStrings.ACTIVATE_SEASON_FORCE_MODAL_TITLE
                      : AppStrings.ACTIVATE_SEASON_MODAL_TITLE}
            </span>
          }
          footer={
            <div>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCloseLifecycleModal}
                data-testid="seasons-list-lifecycle-go-back"
              >
                {AppStrings.DELETE_SEASON_GO_BACK}
              </Button>
              <Button
                intent="danger"
                variant="outline"
                onClick={handleConfirmLifecycleAction}
                data-testid="seasons-list-lifecycle-confirm"
              >
                {pendingLifecycleAction?.kind === "delete"
                  ? AppStrings.DELETE_SEASON_CONFIRM_CTA
                  : pendingLifecycleAction?.kind === "unschedule"
                    ? AppStrings.UNSCHEDULE_SEASON_CONFIRM_CTA
                    : pendingLifecycleAction?.kind === "cancel"
                      ? AppStrings.CANCEL_SEASON_CONFIRM_CTA
                      : pendingLifecycleAction?.force
                        ? AppStrings.ACTIVATE_SEASON_FORCE_CTA
                        : AppStrings.ACTIVATE_SEASON_CONFIRM_CTA}
              </Button>
            </div>
          }
        >
          {pendingLifecycleAction && (
            <>
              <p data-testid="seasons-list-lifecycle-confirm-body">
                {pendingLifecycleAction.kind === "delete"
                  ? `${AppStrings.DELETE_SEASON_CONFIRM_BODY_PREFIX} `
                  : pendingLifecycleAction.kind === "unschedule"
                    ? `${AppStrings.UNSCHEDULE_SEASON_CONFIRM_BODY_PREFIX} `
                    : pendingLifecycleAction.kind === "cancel"
                      ? `${AppStrings.CANCEL_SEASON_CONFIRM_BODY_PREFIX} `
                      : pendingLifecycleAction.force
                        ? `${AppStrings.ACTIVATE_SEASON_FORCE_BODY_PREFIX} `
                        : `${AppStrings.ACTIVATE_SEASON_CONFIRM_BODY_PREFIX} `}
                {pendingLifecycleAction.kind !== "delete" && (
                  <span>{`"${pendingLifecycleAction.season.name}"`}</span>
                )}
                {pendingLifecycleAction.kind === "delete"
                  ? AppStrings.DELETE_SEASON_CONFIRM_BODY_SUFFIX
                  : pendingLifecycleAction.kind === "unschedule"
                    ? AppStrings.UNSCHEDULE_SEASON_CONFIRM_BODY_SUFFIX
                    : pendingLifecycleAction.kind === "cancel"
                      ? AppStrings.CANCEL_SEASON_CONFIRM_BODY_SUFFIX
                      : pendingLifecycleAction.force
                        ? AppStrings.ACTIVATE_SEASON_FORCE_BODY_SUFFIX
                        : AppStrings.ACTIVATE_SEASON_CONFIRM_BODY_SUFFIX}
              </p>
              {pendingLifecycleAction.kind === "activate" &&
                pendingLifecycleAction.force &&
                pendingLifecycleAction.blockerMessage && (
                  <p data-testid="seasons-list-lifecycle-blocker-message">
                    {pendingLifecycleAction.blockerMessage}
                  </p>
                )}
            </>
          )}
        </Modal>
      </div>
    </>
  );
};

export default SeasonsList;
